import { Controller } from "@hotwired/stimulus"

// SCANNING A PACKET, AND ALWAYS HAVING A WAY OUT.
//
// Two decoders behind one control. BarcodeDetector is native, instant and
// Android-only — Safari has it disabled by default as of 26.6 and absent
// before, and every browser on iOS uses Safari's engine, so Chrome for iOS
// does not rescue it. ZXing's WASM build covers everyone else at the cost
// of decoding in JavaScript.
//
// THE TYPED FIELD IS NOT A FAILURE STATE. It is in the sheet from the first
// frame, because decoding is weakest exactly where people scan — bad
// supermarket light, a crumpled label, a curved jar — and somebody who has
// been holding a phone at a packet for ten seconds has already lost. Where
// no camera can run at all, the sheet opens on that field with no apology.
//
// NOTHING IS DECIDED HERE. A successful read navigates to /scan, which owns
// the three-tier lookup and where each outcome lands. This controller's only
// job is turning a camera into digits.
export default class extends Controller {
  static targets = ["video", "camera", "status", "denied", "manual", "typedError"]
  static values = { url: String, origin: String, slot: Object }

  connect() {
    this.stopped = false

    // NOT CACHEABLE, AND THIS IS WHY.
    //
    // Turbo snapshots a page before leaving it and paints that snapshot
    // instantly on a back navigation. A snapshot of this sheet contains a
    // <video> holding the last frame of a camera that has since been
    // stopped — so going back showed a frozen picture of a shelf, the
    // controller reconnected against a dead MediaStream, and nothing ever
    // scanned again. Cody, 11 Sep: "navigating back to a barcode scan
    // doesn't re-fire."
    //
    // Marking the page uncacheable makes Turbo fetch it fresh, which is
    // the only way to get a live camera: a MediaStream cannot be restored
    // from a snapshot, and there is no API that would let it be.
    document.documentElement.setAttribute("data-turbo-cache", "false")

    // See #restore: a browser history restore bypasses Turbo entirely.
    window.addEventListener("pageshow", this.restore)

    this.start()
  }

  disconnect() {
    this.stop()
    window.removeEventListener("pageshow", this.restore)
    // NOT REMOVED HERE. disconnect runs BEFORE Turbo snapshots the page on
    // the way out, so clearing the flag here made the page cacheable again
    // in time to be cached — the opt-out never applied to the one
    // navigation it existed for. The attribute is left for the next page's
    // own controllers to worry about; it only matters while this sheet is
    // the document.
  }

  // A SNAPSHOT CANNOT HOLD A CAMERA.
  //
  // Even opted out of Turbo's cache, a swipe-back is the BROWSER's history
  // restore — bfcache, which Turbo does not control. The page comes back
  // exactly as it left: a <video> element still painted with the last
  // frame, its MediaStream long dead. Cody, 11 Sep: "navigate back (by
  // swiping), it goes back to the scanner but its black and unresponsive."
  //
  // pageshow with persisted=true is the only event that fires for it, and
  // the only honest response is to start the camera over.
  restore = (event) => {
    if (!event.persisted) return

    // The dead stream's tracks are still attached to the <video>, which is
    // why it comes back as a frozen frame rather than an empty box.
    // Releasing first also stops the camera indicator staying lit.
    this.stop()
    this.videoTarget.srcObject = null
    this.cameraTarget.hidden = true

    this.stopped = false
    this.start()
  }

  async start() {
    // No camera API at all — a desktop without one, or a browser that
    // refuses. The typed field is already on screen, so this is a swap of
    // emphasis rather than an error.
    if (!navigator.mediaDevices?.getUserMedia) return this.fallbackToTyping()

    try {
      // facingMode "environment" is the back camera. On a laptop it is
      // ignored and the front one opens, which is fine — the field is what
      // gets used there anyway.
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      })
    } catch (error) {
      // DENIED IS DIFFERENT FROM UNAVAILABLE, and only one of them can be
      // fixed by the person reading the message.
      //
      // A browser will not re-prompt once permission is refused — there is
      // no API to ask again, by design. So "allow the camera" is useless
      // advice; the only way back is through browser settings, and saying
      // so is the difference between a dead end and a fixable one.
      // Cody hit exactly this, 11 Sep.
      return this.fallbackToTyping(error?.name === "NotAllowedError")
    }

    if (this.stopped) return this.releaseCamera()

    this.videoTarget.srcObject = this.stream
    await this.videoTarget.play().catch(() => {})
    this.cameraTarget.hidden = false

    this.detector = await this.buildDetector()
    if (!this.detector) return this.fallbackToTyping()

    this.poll()
  }

  // The native one where it exists, ZXing where it does not. Loaded on
  // demand: the WASM binary is ~200KB and no page should pay for it until
  // somebody actually opens the scanner.
  async buildDetector() {
    if ("BarcodeDetector" in window) {
      try {
        const native = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e"]
        })
        return async (canvas) => {
          const found = await native.detect(canvas)
          return found[0]?.rawValue
        }
      } catch {
        // Constructed but unusable — some builds accept the constructor and
        // then refuse the formats. Fall through to WASM.
      }
    }

    try {
      const { readBarcodesFromImageData } = await import("zxing-wasm/reader")
      return async (canvas) => {
        const context = canvas.getContext("2d", { willReadFrequently: true })
        const image = context.getImageData(0, 0, canvas.width, canvas.height)
        const results = await readBarcodesFromImageData(image, {
          formats: ["EAN-13", "EAN-8", "UPC-A", "UPC-E"],
          tryHarder: true
        })
        return results[0]?.text
      }
    } catch {
      return null
    }
  }

  // FOUR TIMES A SECOND, not every frame. A WASM decode is tens of
  // milliseconds and a phone is holding a camera open at the same time;
  // decoding every frame heats the device and finds the barcode no sooner.
  poll() {
    if (this.stopped) return

    this.timer = setTimeout(() => {
      this.scanFrame().finally(() => this.poll())
    }, 250)
  }

  async scanFrame() {
    const video = this.videoTarget
    if (!video.videoWidth) return

    this.canvas ||= document.createElement("canvas")
    this.canvas.width = video.videoWidth
    this.canvas.height = video.videoHeight
    this.canvas
      .getContext("2d", { willReadFrequently: true })
      .drawImage(video, 0, 0)

    let digits
    try {
      digits = await this.detector(this.canvas)
    } catch {
      // A decoder that throws on one frame is not a decoder that is broken;
      // the next frame is 250ms away.
      return
    }

    if (digits) this.found(digits)
  }

  // Typed rather than scanned. Same destination, same lookup — the only
  // difference is where the digits came from.
  submitTyped(event) {
    event.preventDefault()
    const digits = this.manualTarget.value.replace(/\D/g, "")

    // TOO SHORT IS NOT NOTHING. This used to return in silence, so typing
    // a few digits and pressing the button looked like a dead control —
    // and pressing Enter on a form with one visible field submits it,
    // which is what closed the sheet. Cody, 11 Sep: "i put a random number
    // in and it just closed the modal."
    //
    // MIN_LENGTH is 8 because EAN-8 is the shortest real barcode; anything
    // under it is somebody mid-type or reading the wrong number off a
    // packet, and looking it up could only ever miss.
    if (digits.length < 8) return this.rejectTyped(digits)

    this.clearTypedError()
    this.found(digits)
  }

  // Said in place, beside the field, rather than as a flash somewhere
  // else: the number is still in front of them and the fix is to finish
  // typing it.
  rejectTyped(digits) {
    if (!this.hasTypedErrorTarget) return

    this.typedErrorTarget.textContent = digits.length === 0
      ? "Type the numbers printed under the bars."
      : `That is ${digits.length} digits — a barcode has at least 8.`
    this.typedErrorTarget.hidden = false
    this.manualTarget.focus()
  }

  clearTypedError() {
    if (this.hasTypedErrorTarget) this.typedErrorTarget.hidden = true
  }

  found(digits) {
    if (this.stopped) return

    this.stop()
    // A full navigation rather than a fetch: /scan decides among three
    // destinations, and two of them are different pages. Letting it
    // redirect is simpler than teaching this controller the routes.
    window.location.href = this.buildUrl(digits)
  }

  buildUrl(digits) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("barcode", digits)
    if (this.hasOriginValue) url.searchParams.set("origin", this.originValue)
    if (this.hasSlotValue) {
      for (const [key, value] of Object.entries(this.slotValue)) {
        if (value != null && value !== "") url.searchParams.set(key, value)
      }
    }
    return url.toString()
  }

  fallbackToTyping(denied = false) {
    this.cameraTarget.hidden = true

    if (denied && this.hasDeniedTarget) {
      this.deniedTarget.hidden = false
    } else if (this.hasStatusTarget) {
      this.statusTarget.hidden = false
    }

    // NOT focused when permission was denied: pulling the keyboard up over
    // a message somebody has not read yet hides the one thing that would
    // help them.
    if (!denied) this.manualTarget.focus()
  }

  stop() {
    this.stopped = true
    clearTimeout(this.timer)
    this.releaseCamera()
  }

  releaseCamera() {
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
  }
}
